import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { submitRelevanceReview } from "@/api/relevanceReview";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { verdictLabel } from "./bundleRelevanceStatusPresentation";
import type { RelevanceReviewItem } from "./useBundleRelevanceStatusesForEncounter";

interface RelevanceReviewControlProps {
  encounterId: string;
  reviewItem: RelevanceReviewItem;
}

export function RelevanceReviewControl({ encounterId, reviewItem }: RelevanceReviewControlProps) {
  const { aiRelevant } = reviewItem;
  const queryClient = useQueryClient();
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [correctedRelevant, setCorrectedRelevant] = useState(!aiRelevant);
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { relevant: boolean; reason: string }) =>
      submitRelevanceReview(encounterId, reviewItem.rawBundleId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bundle-relevance-statuses", encounterId] });
      queryClient.invalidateQueries({ queryKey: ["relevance-review-queue"] });
      setIsCorrecting(false);
    },
  });

  return (
    <div className="rounded-md border p-3 space-y-2 text-sm">
      <div>
        AI verdict:{" "}
        <span className="font-medium">
          {aiRelevant === null ? "Not yet available" : aiRelevant ? "Relevant" : "Not relevant"}
        </span>
        {reviewItem.aiVerdictReason && <span className="text-muted-foreground"> — {reviewItem.aiVerdictReason}</span>}
      </div>

      {reviewItem.reviewedAt && !isCorrecting && (
        <div className="text-muted-foreground">
          Reviewed: {verdictLabel(reviewItem.reviewedRelevant)}
          {reviewItem.reviewedReason ? ` — ${reviewItem.reviewedReason}` : ""}
        </div>
      )}

      {!isCorrecting && aiRelevant !== null && (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ relevant: aiRelevant, reason: "" })}
          >
            Agree
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => {
              setCorrectedRelevant(!aiRelevant);
              setReason("");
              setIsCorrecting(true);
            }}
          >
            Override
          </Button>
        </div>
      )}

      {isCorrecting && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={correctedRelevant ? "default" : "outline"}
              onClick={() => setCorrectedRelevant(true)}
            >
              Relevant
            </Button>
            <Button
              size="sm"
              variant={!correctedRelevant ? "default" : "outline"}
              onClick={() => setCorrectedRelevant(false)}
            >
              Not relevant
            </Button>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for correction"
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={mutation.isPending || reason.trim() === ""}
              onClick={() => mutation.mutate({ relevant: correctedRelevant, reason })}
            >
              Submit correction
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIsCorrecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mutation.isError && <div className="text-destructive">{mutation.error.message}</div>}
    </div>
  );
}
